import React, { useEffect, useRef, useState } from "react";
import {
  analyticsUsersApp,
  fetchLTVStats,
  fetchKPIStats,
  getAllCE,
  getAppUsersPaginated
} from "../../../slices/admin/thunk";

import moment from "moment";
import { useDispatch } from "react-redux";
import {
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Spinner,
  TabContent,
  TabPane,
  Table,
  Badge,
} from "reactstrap";
import adminIcon from "../../../assets/images/iconsRoles/adminIcon.svg";
import HeaderRole from "../../../Components/Headers/HeadersRole";
import Loading from "../../../Components/Loading";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import CETable from "../../../Components/Tables/UsersTable/CETable";
import UsersAppTable from "../../../Components/Tables/UsersTable/UsersAppTable";

const BillingAnalyticsTab = ({ isActive }) => {
  const dispatch = useDispatch();
  const [ltvStats, setLtvStats] = useState(null);
  const [kpiStats, setKpiStats] = useState(null);
  const [loadingBillingMetrics, setLoadingBillingMetrics] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async (forceRefresh = false) => {
    try {
      setRefreshing(true);
      await fetchBillingMetrics(forceRefresh);
    } catch (error) {
      console.error("Error refreshing billing analytics:", error);
    } finally {
      setRefreshing(false);
    }
  };

  const fetchBillingMetrics = async (forceRefresh = false) => {
    try {
      setLoadingBillingMetrics(true);

      // Fetch LTV/ARPU stats and KPI stats in parallel
      const [ltvResponse, kpiResponse] = await Promise.all([
        dispatch(fetchLTVStats()),
        dispatch(fetchKPIStats({ forceRefresh }))
      ]);

      if (ltvResponse.payload) {
        setLtvStats(ltvResponse.payload);
      }
      if (kpiResponse.payload) {
        setKpiStats(kpiResponse.payload);
      }

      setLoadingBillingMetrics(false);
    } catch (error) {
      console.error("Error fetching billing analytics:", error);
      setLoadingBillingMetrics(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchBillingMetrics();
    }
  }, [isActive]);

  if (loadingBillingMetrics) {
    return (
      <div className="d-flex justify-content-center py-4">
        <Spinner color="primary" />
      </div>
    );
  }

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  // KPI Cards - Main metrics from KPI endpoint
  const kpiCards = [
    {
      title: 'LTV',
      value: formatCurrency(kpiStats?.ltv),
      description: 'Customer Lifetime Value',
      color: 'primary',
      icon: 'ri-money-dollar-circle-line'
    },
    {
      title: 'Avg Churn Rate',
      value: formatPercent(kpiStats?.averageChurnPercentage),
      description: 'Monthly average churn',
      color: 'warning',
      icon: 'ri-percent-line'
    },
    {
      title: 'Lifespan',
      value: `${kpiStats?.lifespan?.toFixed(1) || '0.0'} months`,
      description: 'Expected customer lifetime',
      color: 'info',
      icon: 'ri-calendar-check-line'
    },
    {
      title: 'Margin',
      value: formatCurrency(kpiStats?.margin),
      description: 'Revenue per month',
      color: 'success',
      icon: 'ri-wallet-3-line'
    },
    {
      title: 'Active Customers',
      value: kpiStats?.totalActiveCustomers || 0,
      description: 'Currently active',
      color: 'success',
      icon: 'ri-user-follow-line'
    },
    {
      title: 'Churned Customers',
      value: kpiStats?.totalChurnedCustomers || 0,
      description: 'Total who canceled',
      color: 'danger',
      icon: 'ri-user-unfollow-line'
    }
  ];

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">KPIs Dashboard</h5>
        <button
          className={`btn btn-soft-primary btn-sm ${refreshing ? 'disabled' : ''}`}
          onClick={() => handleRefresh(true)}
          disabled={refreshing}
        >
          {refreshing ? (
            <Spinner size="sm" color="primary" className="me-1" />
          ) : (
            <i className="ri-refresh-line me-1"></i>
          )}
          Refresh
        </button>
      </div>

      {/* KPI Cards */}
      <div className="row">
        {kpiCards.map((card, index) => (
          <div className="col-md-4 col-sm-6 mb-3" key={index}>
            <Card className="card-animate h-100">
              <CardBody>
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <p className="text-uppercase fw-medium text-muted mb-0">
                      {card.title}
                    </p>
                  </div>
                  <div className="avatar-sm flex-shrink-0">
                    <span className={`avatar-title bg-soft-${card.color} text-${card.color} rounded fs-3`}>
                      <i className={card.icon}></i>
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <h4 className={`fs-22 fw-semibold ff-secondary mb-1 text-${card.color}`}>
                    {card.value}
                  </h4>
                  <small className="text-muted">{card.description}</small>
                </div>
              </CardBody>
            </Card>
          </div>
        ))}
      </div>

      {/* Monthly Churn Table */}
      {kpiStats?.monthlyChurn && kpiStats.monthlyChurn.length > 0 && (
        <Card className="mt-3">
          <CardBody>
            <h6 className="card-title mb-3">Monthly Churn History</h6>
            <div className="table-responsive">
              <Table className="table-hover table-nowrap align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Month</th>
                    <th className="text-center">Active (Start)</th>
                    <th className="text-center">New Signups</th>
                    <th className="text-center">Churned</th>
                    <th className="text-center">Active (End)</th>
                    <th className="text-center">Churn Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {kpiStats.monthlyChurn.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{moment(row.month).format('MMM YYYY')}</strong>
                      </td>
                      <td className="text-center">{row.activeAtStart}</td>
                      <td className="text-center">
                        <Badge color="success" className="fs-6">+{row.newSignups}</Badge>
                      </td>
                      <td className="text-center">
                        {row.churnedDuring > 0 ? (
                          <Badge color="danger" className="fs-6">-{row.churnedDuring}</Badge>
                        ) : (
                          <Badge color="secondary" className="fs-6">0</Badge>
                        )}
                      </td>
                      <td className="text-center">{row.activeAtEnd}</td>
                      <td className="text-center">
                        <Badge
                          color={row.churnPercentage > 5 ? 'danger' : row.churnPercentage > 3 ? 'warning' : 'success'}
                          className="fs-6"
                        >
                          {row.churnPercentage.toFixed(1)}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Additional LTV/ARPU Details */}
      <div className="row mt-3">
        <div className="col-md-6">
          <Card>
            <CardBody>
              <h6 className="card-title mb-3">Revenue Details</h6>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Total Revenue:</span>
                <strong>{formatCurrency(ltvStats?.totalRevenue)}</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Active Revenue:</span>
                <strong>{formatCurrency(ltvStats?.totalActiveRevenue)}</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">ARPU (All Users):</span>
                <strong>{formatCurrency(ltvStats?.arpu)}</strong>
              </div>
            </CardBody>
          </Card>
        </div>
        <div className="col-md-6">
          <Card>
            <CardBody>
              <h6 className="card-title mb-3">Subscription Details</h6>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Avg Subscription (All):</span>
                <strong>{ltvStats?.averageSubscriptionMonths?.toFixed(2) || '0.00'} months</strong>
              </div>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Avg Subscription (Churned):</span>
                <strong>{ltvStats?.averageChurnedSubscriptionMonths?.toFixed(2) || '0.00'} months</strong>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Total Customers:</span>
                <strong>{ltvStats?.totalCustomers || 0}</strong>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>

      {kpiStats?.calculatedAt && (
        <small className="text-muted d-block mt-3">
          *Data from DEX. Last calculated: {moment(kpiStats.calculatedAt).format('MMM D, YYYY h:mm A')}
        </small>
      )}
    </div>
  );
};



const AppUsersDashboard = () => {
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState({});
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingCE, setLoadingCE] = useState(false);

  const [currentPageUsers, setCurrentPageUsers] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(0);

  const [dataCE, setDataCE] = useState([]);
  const [totalItemsCE, setTotalItemsCE] = useState(0);
  const [totalPagesCE, setTotalPagesCE] = useState(0);
  const [pageSizeCE, setPageSizeCE] = useState(0);
  const [currentPageCE, setCurrentPageCE] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  const [searchDone, setSearchDone] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState(searchTerm);

  const [activeTab, setActiveTab] = useState('users');

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const fetchUsersApp = async () => {
    try {
      setLoading(true);

      const response = await dispatch(
        getAppUsersPaginated({
          page: currentPageUsers,
          query: searchTerm,
          filterParam: '',
        })
      );

      const res = response.payload;
      if (res && res.data) {
        setUsers(res.data);
        setPageSize(res.pageSize);
        setTotalItems(res.total);
        setTotalPages(res.pages);
      }
      setSearchDone(true);
      setLoading(false);
    } catch (error) {
      console.error("fetchUsersApp -> error", error);
      setLoading(false);
    }
  };

  const fetchAnalyticsUsersApp = async () => {
    try {
      setLoadingAnalytics(true);
      const response = await dispatch(analyticsUsersApp());

      const res = response.payload;
      if (res && !res.error) {
        setAnalytics(res);
      }
      setLoadingAnalytics(false);
    } catch (error) {
      console.error("fetchUsersApp -> error", error);
      setLoadingAnalytics(false);
    }
  };

  const fetchCE = async () => {
    try {
      setLoadingCE(true);
      const response = await dispatch(getAllCE({ page: currentPageCE }));
      const res = response.payload;
      if (res && res.data) {
        setDataCE(res.data);
        setPageSizeCE(res.pageSize);
        setTotalItemsCE(res.total);
        setTotalPagesCE(res.pages);
      }

      setLoadingCE(false);
    } catch (error) {
      console.error("fetchCE -> error", error);
      setLoadingCE(false);
    }
  };

  useEffect(() => {
    if (debouncedSearchTerm === lastSearchTerm) {
      setSearchDone(true);
    } else {
      setSearchDone(false);
    }
    fetchUsersApp();
  }, [currentPageUsers, debouncedSearchTerm]);

  useEffect(() => {
    fetchAnalyticsUsersApp();
  }, []);

  useEffect(() => {
    fetchCE();
  }, [currentPageCE]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPageUsers(0);
    }, 500);

    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const formatDate = (dateInput) => {
    if (!dateInput) return null;

    let date = moment.utc(
      typeof dateInput === "number" ? dateInput * 1000 : dateInput
    );
    return date.format("DD/MM/YYYY");
  };

  const textAnalytics = loadingAnalytics ? (
    <Spinner size="sm" color="primary" />
  ) : (
    <div>
      <h6 className="text-muted fw-semibold">
        Total Users:
        <span className="ms-1 text-dark fw-bold">
          {analytics?.totalUsers || 0}
        </span>
      </h6>
      <h6 className="text-muted fw-semibold">
        Subscribed:
        <span className="ms-1 text-dark fw-bold">
          {analytics?.subscribedUsers || 0}
        </span>
      </h6>
      {analytics?.activeEmployees && (
        <h6 className="text-muted fw-semibold">
          Active Employees:
          <span className="ms-1 text-dark fw-bold">
            {analytics?.activeEmployees}
          </span>
        </h6>
      )}
    </div>
  );

  return (
    <div className="page-content">
      <HeaderRole
        role={"Members Stats"}
        icon={adminIcon}
        svg={true}
        text={textAnalytics}
      />

      {/* Navigation Tabs */}
      <Nav tabs className="mb-3 nav-tabs-custom">
        <NavItem>
          <NavLink
            style={{ cursor: 'pointer' }}
            className={activeTab === 'users' ? 'active' : ''}
            onClick={() => toggleTab('users')}
          >
            Members
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            style={{ cursor: 'pointer' }}
            className={activeTab === 'ce' ? 'active' : ''}
            onClick={() => toggleTab('ce')}
          >
            Client Eligibilities
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            style={{ cursor: 'pointer' }}
            className={activeTab === 'billing' ? 'active' : ''}
            onClick={() => toggleTab('billing')}
          >
            Analytics
          </NavLink>
        </NavItem>
      </Nav>

      <TabContent activeTab={activeTab}>
        {/* Users Tab */}
        <TabPane tabId="users">
          <Card>
            <CardBody className="py-3">
              <div className="col-12 d-flex justify-content-between align-items-center">
                <div className="col-3 d-flex justify-content-start">
                  <SearchBar
                    inputRef={inputRef}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Filter by name or email"
                    shouldDisplaySearchBar={true}
                  />
                </div>
              </div>
              <div className="table-container mt-2 position-relative">
                {loading && <Loading />}
                <div className="live-preview">
                  <div className="table-responsive">
                    <UsersAppTable
                      data={users}
                      searchDone={searchDone}
                      searchParam={debouncedSearchTerm}
                      onRefresh={fetchUsersApp}
                      formatDate={formatDate}
                      paginationProps={{
                        currentPage: currentPageUsers,
                        totalPages,
                        totalItems,
                        pageSize,
                        onChangePage: setCurrentPageUsers,
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabPane>

        {/* Client Eligibilities Tab */}
        <TabPane tabId="ce">
          <Card className="mb-0">
            <CardBody className="py-3">
              <div className="table-container position-relative">
                {loadingCE && <Loading />}
                <div className="live-preview">
                  <div className="table-responsive">
                    <CETable
                      data={dataCE}
                      setData={setDataCE}
                      loading={loadingCE}
                      paginationProps={{
                        currentPage: currentPageCE,
                        totalPages: totalPagesCE,
                        totalItems: totalItemsCE,
                        pageSize: pageSizeCE,
                        onChangePage: setCurrentPageCE,
                      }}
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </TabPane>

        {/* Billing Analytics Tab */}
        <TabPane tabId="billing">
          <BillingAnalyticsTab
            isActive={activeTab === 'billing'}
          />
        </TabPane>
      </TabContent>
    </div>
  );
};

export default AppUsersDashboard;